import React, { useState, useCallback, useEffect } from 'react';
import { 
  Play, 
  Pause, 
  RotateCcw, 
  CheckCircle, 
  Clock, 
  Users, 
  Target, 
  Compass, 
  Ruler, 
  Calculator,
  Shapes,
  Repeat
} from 'lucide-react';

interface Phase {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  skills: string[];
  tasks: string[];
}

interface ProjectWorkspaceResource {
  type: 'project_workspace';
  name: string;
  title: string;
  description: string;
  project_theme: string;
  phases: Phase[];
  collaboration_enabled: boolean;
  peer_review_enabled: boolean;
}

interface ProjectWorkspaceComponentProps {
  resource: ProjectWorkspaceResource;
  onAction?: (action: string, data: any) => void;
}

export const ProjectWorkspaceComponent: React.FC<ProjectWorkspaceComponentProps> = ({
  resource,
  onAction
}) => {
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [timeRemaining, setTimeRemaining] = useState(0);

  const currentPhase = resource.phases[currentPhaseIndex];

  // Timer pour la phase actuelle
  useEffect(() => {
    if (isActive && timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && isActive) {
      // Phase terminée
      setIsActive(false);
      if (onAction) {
        onAction('phase_completed', { phaseId: currentPhase.id, phaseIndex: currentPhaseIndex });
      }
    }
  }, [isActive, timeRemaining, currentPhase, currentPhaseIndex, onAction]);

  const startPhase = useCallback(() => {
    setIsActive(true);
    setTimeRemaining(currentPhase.duration_minutes * 60);
    if (onAction) {
      onAction('phase_started', { phaseId: currentPhase.id, phaseIndex: currentPhaseIndex });
    }
  }, [currentPhase, currentPhaseIndex, onAction]);

  const pausePhase = useCallback(() => {
    setIsActive(false);
    if (onAction) {
      onAction('phase_paused', { phaseId: currentPhase.id, phaseIndex: currentPhaseIndex });
    }
  }, [currentPhase, currentPhaseIndex, onAction]);

  const resetPhase = useCallback(() => {
    setIsActive(false);
    setTimeRemaining(currentPhase.duration_minutes * 60);
    setCompletedTasks(new Set());
    if (onAction) {
      onAction('phase_reset', { phaseId: currentPhase.id, phaseIndex: currentPhaseIndex });
    }
  }, [currentPhase, currentPhaseIndex, onAction]);

  const nextPhase = useCallback(() => {
    if (currentPhaseIndex < resource.phases.length - 1) {
      setCurrentPhaseIndex(prev => prev + 1);
      setCompletedTasks(new Set());
      setIsActive(false);
      if (onAction) {
        onAction('phase_changed', { 
          fromPhase: currentPhase.id, 
          toPhase: resource.phases[currentPhaseIndex + 1].id,
          phaseIndex: currentPhaseIndex + 1
        });
      }
    }
  }, [currentPhaseIndex, resource.phases, currentPhase, onAction]);

  const completeTask = useCallback((task: string) => {
    setCompletedTasks(prev => new Set(prev).add(task));
    if (onAction) {
      onAction('task_completed', { 
        phaseId: currentPhase.id, 
        task, 
        completedTasks: completedTasks.size + 1,
        totalTasks: currentPhase.tasks.length
      });
    }
  }, [currentPhase, completedTasks, onAction]);

  const getSkillIcon = (skill: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      sorting: Target,
      spatial: Compass,
      geometry: Shapes,
      measurement: Ruler,
      logic: Repeat,
      calculation: Calculator
    };
    return iconMap[skill] || Target;
  };

  const getSkillColor = (skill: string) => {
    const colorMap: { [key: string]: string } = {
      sorting: 'bg-blue-100 text-blue-800',
      spatial: 'bg-green-100 text-green-800',
      geometry: 'bg-purple-100 text-purple-800',
      measurement: 'bg-yellow-100 text-yellow-800',
      logic: 'bg-pink-100 text-pink-800',
      calculation: 'bg-red-100 text-red-800'
    };
    return colorMap[skill] || 'bg-gray-100 text-gray-800';
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = (completedTasks.size / currentPhase.tasks.length) * 100;

  return (
    <div className="w-full p-3 bg-white rounded-lg shadow-sm">
      {/* Header compact */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{resource.title}</h3>
        <p className="text-sm text-gray-600 mb-3">{resource.description}</p>
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-gray-800">Thème du Projet</h4>
          <p className="text-sm text-gray-700">{resource.project_theme}</p>
        </div>
      </div>

      {/* Phase actuelle - Compact */}
      <div className="bg-blue-50 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Phase {currentPhaseIndex + 1}: {currentPhase.name}
            </h4>
            <p className="text-xs text-blue-700">{currentPhase.description}</p>
          </div>
          <div className="text-right ml-3">
            <div className="text-lg font-bold text-blue-900">
              {formatTime(timeRemaining)}
            </div>
            <div className="text-xs text-blue-600">
              {currentPhase.duration_minutes} min
            </div>
          </div>
        </div>

        {/* Contrôles de phase - Compact */}
        <div className="flex items-center justify-center space-x-2 mb-3">
          {!isActive ? (
            <button
              onClick={startPhase}
              className="px-3 py-2 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition-colors flex items-center space-x-1"
            >
              <Play className="w-3 h-3" />
              <span>Commencer</span>
            </button>
          ) : (
            <button
              onClick={pausePhase}
              className="px-3 py-2 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 transition-colors flex items-center space-x-1"
            >
              <Pause className="w-3 h-3" />
              <span>Pause</span>
            </button>
          )}
          
          <button
            onClick={resetPhase}
            className="px-3 py-2 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition-colors flex items-center space-x-1"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Reset</span>
          </button>
        </div>

        {/* Progression de la phase - Compact */}
        <div className="mb-2">
          <div className="flex justify-between text-xs text-blue-700 mb-1">
            <span>Progression</span>
            <span>{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Compétences requises - Compact */}
      <div className="mb-4">
        <h5 className="text-sm font-semibold text-gray-900 mb-2">Compétences requises</h5>
        <div className="flex flex-wrap gap-1">
          {currentPhase.skills.map((skill, index) => {
            const Icon = getSkillIcon(skill);
            return (
              <div
                key={index}
                className={`px-2 py-1 rounded-full flex items-center space-x-1 ${getSkillColor(skill)}`}
              >
                <Icon className="w-3 h-3" />
                <span className="text-xs font-medium capitalize">{skill}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tâches de la phase - Compact */}
      <div className="mb-4">
        <h5 className="text-sm font-semibold text-gray-900 mb-2">Tâches à accomplir</h5>
        <div className="space-y-2 max-h-32 overflow-y-auto">
          {currentPhase.tasks.map((task, index) => {
            const isCompleted = completedTasks.has(task);
            return (
              <div
                key={index}
                className={`flex items-center space-x-2 p-2 rounded border ${
                  isCompleted 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-200 bg-white'
                }`}
              >
                {isCompleted ? (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <div className="w-4 h-4 border-2 border-gray-300 rounded-full flex-shrink-0" />
                )}
                <span className={`flex-1 text-xs ${isCompleted ? 'text-green-700' : 'text-gray-700'}`}>
                  {task}
                </span>
                {!isCompleted && (
                  <button
                    onClick={() => completeTask(task)}
                    className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs flex-shrink-0"
                  >
                    ✓
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation entre phases - Compact */}
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-gray-600">
          Phase {currentPhaseIndex + 1}/{resource.phases.length}
        </div>
        
        {currentPhaseIndex < resource.phases.length - 1 && (
          <button
            onClick={nextPhase}
            className="px-3 py-2 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors flex items-center space-x-1"
          >
            <span>Suivante</span>
            <Clock className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Indicateurs de collaboration - Compact */}
      {resource.collaboration_enabled && (
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 text-green-700">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Collaboration activée</span>
          </div>
          <p className="text-xs text-green-600 mt-1">
            Travaillez avec votre équipe
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectWorkspaceComponent;
